import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api from "../api/axios"; // use your existing axios instance with interceptors
import { useGlobal } from "../context/GlobalContext";

const AuthSection = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { syncGuestCart, fetchCart } = useGlobal();

    const [activeTab, setActiveTab] = useState("login");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [globalError, setGlobalError] = useState("");
    const [globalSuccess, setGlobalSuccess] = useState("");

    // Form states
    const [loginForm, setLoginForm] = useState({ email: "", password: "" });
    const [loginErrors, setLoginErrors] = useState({});

    const [registerForm, setRegisterForm] = useState({
        name: "", phone: "", email: "", password: "", confirmPassword: ""
    });
    const [registerErrors, setRegisterErrors] = useState({});

    const [showLoginPassword, setShowLoginPassword] = useState(false);
    const [showRegisterPassword, setShowRegisterPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        if (location.pathname.includes('register')) {
            setActiveTab("register");
        } else {
            setActiveTab("login");
        }
    }, [location.pathname]);

    const handleTabChange = (tab) => {
        setGlobalError("");
        setGlobalSuccess("");
        setActiveTab(tab);
        const search = location.search || "";
        if (tab === "register") {
            navigate("/register" + search, { replace: true, state: location.state });
        } else {
            navigate("/login" + search, { replace: true, state: location.state });
        }
    };

    // Validations
    const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const validatePhone = (phone) => /^\d{10}$/.test(phone?.trim());

    const getRedirectDestination = () => {
        const params = new URLSearchParams(location.search);
        const redirectParam = params.get('redirect');
        if (redirectParam && redirectParam.startsWith('/')) {
            return redirectParam;
        }
        if (location.state?.from && typeof location.state.from === 'string' && location.state.from.startsWith('/')) {
            return location.state.from;
        }
        return "/";
    };

    const handleLoginSubmit = async (e) => {
        e.preventDefault();
        setGlobalError("");
        setGlobalSuccess("");

        let errors = {};
        if (!loginForm.email.trim()) errors.email = "Enter your email address";
        else if (!validateEmail(loginForm.email)) errors.email = "Enter a valid email address";

        if (!loginForm.password.trim()) errors.password = "Enter your password";

        setLoginErrors(errors);

        if (Object.keys(errors).length > 0) return;

        setIsSubmitting(true);
        try {
            const res = await api.post("/api/auth/login", {
                email: loginForm.email,
                password: loginForm.password
            });

            if (res.data && res.data.token) {
                localStorage.setItem("token", res.data.token);
                localStorage.setItem("userName", res.data.name);
                localStorage.setItem("userEmail", res.data.email);
                localStorage.setItem("userPhone", res.data.phone || "");

                // Sync local storage guest cart into user account cart
                if (syncGuestCart) {
                    await syncGuestCart();
                }
                if (fetchCart) {
                    await fetchCart();
                }

                const targetDestination = getRedirectDestination();
                setGlobalSuccess("Login successful! Redirecting...");
                setTimeout(() => {
                    navigate(targetDestination, { state: location.state });
                    window.location.reload(); // Quick refresh to update Header state
                }, 800);
            }
        } catch (error) {
            setGlobalError(error.response?.data?.message || "Invalid credentials or server error.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        setGlobalError("");
        setGlobalSuccess("");

        let errors = {};
        if (!registerForm.name.trim()) errors.name = "Enter your name";

        if (!registerForm.phone.trim()) errors.phone = "Enter your phone number";
        else if (!validatePhone(registerForm.phone)) errors.phone = "Enter a valid 10-digit phone number";

        if (!registerForm.email.trim()) errors.email = "Enter your email address";
        else if (!validateEmail(registerForm.email)) errors.email = "Enter a valid email address";

        if (!registerForm.password.trim()) errors.password = "Enter your password";
        else if (registerForm.password.length < 6) errors.password = "Password must be at least 6 characters";

        if (!registerForm.confirmPassword.trim()) errors.confirmPassword = "Confirm your password";
        else if (registerForm.password !== registerForm.confirmPassword) errors.confirmPassword = "Passwords do not match";

        setRegisterErrors(errors);

        if (Object.keys(errors).length > 0) return;

        setIsSubmitting(true);
        try {
            const res = await api.post("/api/auth/register", {
                name: registerForm.name,
                email: registerForm.email,
                phone: registerForm.phone,
                password: registerForm.password
            });

            if (res.data && res.data.token) {
                localStorage.setItem("token", res.data.token);
                localStorage.setItem("userName", res.data.name);
                localStorage.setItem("userEmail", res.data.email);
                localStorage.setItem("userPhone", res.data.phone || "");

                // Sync local storage guest cart into user account cart
                if (syncGuestCart) {
                    await syncGuestCart();
                }
                if (fetchCart) {
                    await fetchCart();
                }

                const targetDestination = getRedirectDestination();
                setGlobalSuccess("Registration successful! Redirecting...");
                setTimeout(() => {
                    navigate(targetDestination, { state: location.state });
                    window.location.reload();
                }, 800);
            }
        } catch (error) {
            setGlobalError(error.response?.data?.message || "Registration failed. Try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="auth-section py-80 bg-gray-50 flex-center" style={{ minHeight: '600px', backgroundColor: '#fdfbfb' }}>
            <style>{`
                .auth-card {
                    background: #fff;
                    border-radius: 20px;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.06);
                    width: 100%;
                    max-width: 480px;
                    padding: 40px;
                    margin: 0 auto;
                }
                .auth-tabs {
                    display: flex;
                    border-bottom: 2px solid #EFEFEF;
                    margin-bottom: 32px;
                }
                .auth-tab-btn {
                    flex: 1;
                    text-align: center;
                    background: none;
                    border: none;
                    padding: 14px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: #9AA0A6;
                    cursor: pointer;
                    position: relative;
                    transition: color 0.3s;
                }
                .auth-tab-btn.active {
                    color: var(--main-600, #2FA84F);
                }
                .auth-tab-btn.active::after {
                    content: '';
                    position: absolute;
                    left: 0;
                    bottom: -2px;
                    width: 100%;
                    height: 2px;
                    background-color: var(--main-600, #2FA84F);
                }
                .auth-input-group {
                    position: relative;
                    margin-bottom: 20px;
                }
                .auth-input-group i.left-icon {
                    position: absolute;
                    top: 14px;
                    left: 16px;
                    font-size: 20px;
                    color: var(--main-600, #2FA84F);
                }
                .auth-input-group i.right-icon {
                    position: absolute;
                    top: 14px;
                    right: 16px;
                    font-size: 20px;
                    color: #9AA0A6;
                    cursor: pointer;
                    transition: color 0.3s;
                }
                .auth-input-group i.right-icon:hover {
                    color: var(--main-600, #2FA84F);
                }
                .auth-input {
                    width: 100%;
                    border: 1px solid #EFEFEF;
                    border-radius: 12px;
                    padding: 14px 46px 14px 46px; 
                    font-size: 14px;
                    color: #1B1B1B;
                    outline: none;
                    transition: border 0.3s;
                }
                .auth-input-no-right {
                    padding-right: 16px;
                }
                .auth-input:focus {
                    border-color: var(--main-600, #2FA84F);
                }
                .auth-error-msg {
                    color: #E0503A;
                    font-size: 12px;
                    margin-top: 6px;
                    margin-left: 12px;
                    display: block;
                }
                .auth-submit-btn {
                    width: 100%;
                    background: var(--main-600, #2FA84F);
                    color: #fff;
                    padding: 14px;
                    border: none;
                    border-radius: 12px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    transition: background 0.3s;
                    margin-top: 12px;
                }
                .auth-submit-btn:hover {
                    background: var(--main-700, #268940);
                }
                .auth-submit-btn:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
                .global-msg {
                    padding: 12px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    font-size: 14px;
                    text-align: center;
                }
                .global-msg.error { background: #FDF3F2; color: #E0503A; border: 1px solid #F3D4D4; }
                .global-msg.success { background: #E7F7EC; color: #2FA84F; border: 1px solid #c3e8cd; }
                
                @media (max-width: 575px) {
                    .auth-card { padding: 24px; }
                }
            `}</style>

            <div className="container">
                <div className="auth-card wow zoomIn" data-wow-duration="0.75s">
                    <div className="auth-tabs">
                        <button
                            className={`auth-tab-btn ${activeTab === 'login' ? 'active' : ''}`}
                            onClick={() => handleTabChange('login')}
                        >
                            Login
                        </button>
                        <button
                            className={`auth-tab-btn ${activeTab === 'register' ? 'active' : ''}`}
                            onClick={() => handleTabChange('register')}
                        >
                            Register
                        </button>
                    </div>

                    {globalError && <div className="global-msg error">{globalError}</div>}
                    {globalSuccess && <div className="global-msg success">{globalSuccess}</div>}

                    {activeTab === 'login' && (
                        <form onSubmit={handleLoginSubmit} autoComplete="off">
                            <div className="auth-input-group">
                                <i className="ph ph-envelope-simple left-icon" />
                                <input
                                    type="email"
                                    className="auth-input auth-input-no-right"
                                    placeholder="Email Address"
                                    value={loginForm.email}
                                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                                    autoComplete="off"
                                    name="user_email_login_disabler"
                                />
                                {loginErrors.email && <span className="auth-error-msg">{loginErrors.email}</span>}
                            </div>

                            <div className="auth-input-group">
                                <i className="ph ph-lock-key left-icon" />
                                <input
                                    type={showLoginPassword ? "text" : "password"}
                                    className="auth-input"
                                    placeholder="Password"
                                    value={loginForm.password}
                                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                    autoComplete="new-password"
                                    name="user_password_login_disabler"
                                />
                                <i
                                    className={`ph ${showLoginPassword ? 'ph-eye-slash' : 'ph-eye'} right-icon`}
                                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                                />
                                {loginErrors.password && <span className="auth-error-msg">{loginErrors.password}</span>}
                            </div>

                            <div className="flex-between mb-24 mt-12 px-8">
                                <label className="flex-align gap-8 text-sm text-gray-700 cursor-pointer">
                                    <input type="checkbox" className="form-check-input" />
                                    Remember me
                                </label>
                                <Link to="/forgot-password" className="text-sm text-main-600 hover-text-decoration-underline fw-medium">
                                    Forgot Password?
                                </Link>
                            </div>

                            <button type="submit" className="auth-submit-btn" disabled={isSubmitting}>
                                {isSubmitting ? 'Logging in...' : 'Login Now'} <i className="ph ph-arrow-right" />
                            </button>
                        </form>
                    )}

                    {activeTab === 'register' && (
                        <form onSubmit={handleRegisterSubmit} autoComplete="off">
                            <div className="auth-input-group">
                                <i className="ph ph-user left-icon" />
                                <input
                                    type="text"
                                    className="auth-input auth-input-no-right"
                                    placeholder="Full Name"
                                    value={registerForm.name}
                                    onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                                    autoComplete="off"
                                />
                                {registerErrors.name && <span className="auth-error-msg">{registerErrors.name}</span>}
                            </div>

                            <div className="auth-input-group">
                                <i className="ph ph-phone left-icon" />
                                <input
                                    type="tel"
                                    className="auth-input auth-input-no-right"
                                    placeholder="Phone Number (10 digits)"
                                    value={registerForm.phone}
                                    onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value })}
                                />
                                {registerErrors.phone && <span className="auth-error-msg">{registerErrors.phone}</span>}
                            </div>

                            <div className="auth-input-group">
                                <i className="ph ph-envelope-simple left-icon" />
                                <input
                                    type="email"
                                    className="auth-input auth-input-no-right"
                                    placeholder="Email Address"
                                    value={registerForm.email}
                                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                                    autoComplete="off"
                                    name="user_email_register_disabler"
                                />
                                {registerErrors.email && <span className="auth-error-msg">{registerErrors.email}</span>}
                            </div>

                            <div className="auth-input-group">
                                <i className="ph ph-lock-key left-icon" />
                                <input
                                    type={showRegisterPassword ? "text" : "password"}
                                    className="auth-input"
                                    placeholder="Password"
                                    value={registerForm.password}
                                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                                    autoComplete="new-password"
                                />
                                <i
                                    className={`ph ${showRegisterPassword ? 'ph-eye-slash' : 'ph-eye'} right-icon`}
                                    onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                                />
                                {registerErrors.password && <span className="auth-error-msg">{registerErrors.password}</span>}
                            </div>

                            <div className="auth-input-group">
                                <i className="ph ph-lock-key left-icon" />
                                <input
                                    type={showConfirmPassword ? "text" : "password"}
                                    className="auth-input"
                                    placeholder="Confirm Password"
                                    value={registerForm.confirmPassword}
                                    onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                                    autoComplete="new-password"
                                />
                                <i
                                    className={`ph ${showConfirmPassword ? 'ph-eye-slash' : 'ph-eye'} right-icon`}
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                />
                                {registerErrors.confirmPassword && <span className="auth-error-msg">{registerErrors.confirmPassword}</span>}
                            </div>

                            <button type="submit" className="auth-submit-btn" disabled={isSubmitting}>
                                {isSubmitting ? 'Registering...' : 'Register Now'} <i className="ph ph-arrow-right" />
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};

export default AuthSection;
